import Utils from "common/utils";
import Constants from 'common/constants'
import WeatherParams from "models/weatherParams";
import Analytics from "analytics";
import ListenerManager from "manager/listenerManager";
import DataSource from 'common/dataStore'
import UserSelectedLocationsDataSource from 'dataSource/userSelectedLocationsDataSource';
import DeviceTokenManger from 'manager/deviceTokenManager'

const LOG_PREFIX = "[LOCATION_STORE] "

class LocationStore {
    constructor() {
        this.storedLocations = []
        this.currentLocationIndex = -1

        this.isCurrentLocationFromGeoLocation = false
        this.geoLocation = null

        this.newManualLocationCbs = new ListenerManager()
        this.newGeoLocationCbs = new ListenerManager()
        // Fetch the data from the store
        if (false === Utils.isNull(DataSource)) {
            try {
                let cachedData = DataSource.getItem(Constants.LOCATION_STORE.LOCATION_STORE_KEY)
                if (false === Utils.isNull(cachedData)) {
                    this.storedLocations = JSON.parse(cachedData)
                }
            } catch (err) {
                console.log(LOG_PREFIX, "Error fetching the stored-locations from the local-storage - ", err)
            }

            try {
                let cachedCurrentLocIndexStr = DataSource.getItem(Constants.LOCATION_STORE.CURRENT_LOCATION_INDEX)
                let cachedCurrentLocIndex = parseInt(cachedCurrentLocIndexStr, 10)
                if (false === Utils.isNull(cachedCurrentLocIndex)) {
                    // NOTE: Wantonly not triggering callbacks
                    this.setCurrentLocationIndex(cachedCurrentLocIndex)
                }
            } catch (err) {
                console.log(LOG_PREFIX, "Error fetching the current-location data from the store - ", err)
            }

            try {
                let cachedIsCurrGeoLoc = DataSource.getItem(Constants.LOCATION_STORE.IS_CURR_LOC_FROM_GEO)
                let isGeo = false
                if (JSON.parse(cachedIsCurrGeoLoc) === true) {
                    isGeo = true
                }
                this.isCurrentLocationFromGeoLocation = isGeo
            } catch (err) {
                console.log(LOG_PREFIX, "Error fetching is-location-from-geo data", err)
            }

            try {
                let cachedGeoLoc = DataSource.getItem(Constants.LOCATION_STORE.GEO_LOCATION)
                this.geoLocation = JSON.parse(cachedGeoLoc)
            } catch (err) {
                console.log(LOG_PREFIX, "Error fetching the geo-data", err)
            }
        }
    }

    /**
     * Method to allow registration of callbacks when a new-location is added to the store.
     * NOTE: This will not be called when a new geo-location is added to the store. Only manual locations
     * @param {function} cb
     * @returns {string|null} Listener-id
     */
    registerCbForNewManualLocation(cb) {
        if (Utils.isNull(cb) || typeof cb !== "function") {
            return null
        }
        return this.newManualLocationCbs.addListener(cb)
    }

    /**
     * Method to unregister callbacks.
     * @param {string} key
     * @returns {boolean}
     */
    unregisterCbNewManualLocationForKey(key) {
        return this.newManualLocationCbs.removeListener(key)
    }

    /**
     * Method to allow registration of callbacks when a new-geo-location is added to the store.
     * NOTE: This will not be called when a new manual-location is added to the store. Only manual locations
     * @param {function} cb
     * @returns {string|null} Listener-id
     */
    registerCbForNewGeoLocation(cb) {
        if (Utils.isNull(cb) || typeof cb !== "function") {
            return null
        }
        return this.newGeoLocationCbs.addListener(cb)
    }

    /**
     * Method to unregister callbacks.
     * @param {string} key
     * @returns {boolean}
     */
    unregisterCbNewGeoLocationForKey(key) {
        return this.newGeoLocationCbs.removeListener(key)
    }

    sendSelectedLocations() {
        try {
            let showPremiumFeature = DataSource.getItem(Constants.FCM_STORE.SHOW_PREMIUM_FEATURE);
            let { deviceID = '', fcmToken = '' } = DeviceTokenManger.fetchToken() || {}
            if (!showPremiumFeature || !deviceID || !fcmToken) {
                console.log('Missing Data or hidden premium features')
                return
            }
            UserSelectedLocationsDataSource.sendSelectedLocations()
        } catch (e) {
            console.log(e)
        }
    }

    /**
     * @param {WeatherParams} location
     * @returns {boolean} Indicates whether the location was added successfully
     */
    addAndSendLocation(location, updateCurrentIndex = true) {
        if (Utils.isNull(location) || false === (location instanceof WeatherParams)) {
            console.log(LOG_PREFIX, "Not adding the location to the store since the argument is invalid")
            return false
        }

        // Handle addition of geo-location
        if (typeof location.weatherSource === 'undefined' || location.weatherSource === Constants.WEATHER_SRC.NATIVE_GEO) {
            console.log(LOG_PREFIX, "Not adding the location since it's src is native-geo")
            let didAddLoc = this.__addGeoLocationToStore(location)
            if (false === didAddLoc) {
                console.log(LOG_PREFIX, "Not adding geo-location since it's returned false")
                return false
            }
            this.sendSelectedLocations()
            return true
        }

        // Handle addition of the manual-locations

        // Check if the location is already present in the store
        let locVal = this.isLocationAlreadyPresent(location)
        if (locVal !== -1) {
            console.log(LOG_PREFIX, "Skip adding location since it's already present in the store")

            // Update the correct index if the location is already present.
            if (true === updateCurrentIndex) {
                this.setCurrentLocationIndex(locVal)
            }
            return true

        } else {
            // Add to store
            console.log(LOG_PREFIX, "Adding to the store - ", location)
            try {
                this.storedLocations.push(Object.assign({}, location))
                this.updateLocalStoreForLocations()
                this.sendSelectedLocations()
            } catch (err) {
                console.log(LOG_PREFIX, "Error storing location into the local-storage - ", err)
            }

            // Update the index of the latest entry
            if (true === updateCurrentIndex) {
                this.setCurrentLocationIndex(this.storedLocations.length - 1)
            }

            Analytics.sendEvent(
                Constants.ANALYTICS.EVENTS.CATEGORY.LOCATION,
                Constants.ANALYTICS.EVENTS.ACTION.ADD_LOCATION,
            )
        }

        // Call all the callbacks (async)
        this.callLocationCbsAsync(this.newManualLocationCbs)

        // NOTE: Returning true even if not cached into the local-store purposefully.
        // What if the DataSource is not available? Something is better than nothing ;)
        return true
    }

    /**
     * @param {WeatherParams} location
     * @returns {boolean} Indicates whether the location was added successfully
     */
    __addGeoLocationToStore(location) {
        if (Utils.isNull(location) || false === (location instanceof WeatherParams)) {
            console.log(LOG_PREFIX, "Not adding the location to the store since the argument is invalid")
            return false
        } else if (location.weatherSource !== Constants.WEATHER_SRC.NATIVE_GEO) {
            console.log(LOG_PREFIX, "Not adding geo-location since it's not of the correct type")
            return false
        }

        this.geoLocation = Object.assign({}, location)
        this.updateLocalStoreForGeoLocation()

        this.callLocationCbsAsync(this.newGeoLocationCbs)
        return true
    }

    /**
     * @param {ListenerManager}
     */
    callLocationCbsAsync(locCbsManager) {
        return new Promise((resolve, reject) => {
            if (Utils.isNull(locCbsManager)) {
                reject(new Error("Cannot find any location callbacks!"))
                return
            }

            let locationCbs = locCbsManager.getAllListeners()
            for (let i = 0; i < locationCbs.length; i++) {
                let cb = locationCbs[i]
                try {
                    cb()
                } catch (err) {
                    console.log(LOG_PREFIX, "Error calling the callback - ", err)
                }
            }
            resolve()
        })
    }

    /**
     * Remove a particular location entry from the store.
     * @param {int} index
     * @returns {{status: {boolean}, hasCurrentLocationChanged: {boolean}}}
     * Status is false if removal failed.
     * hasCurrentLocationChanged is set if the current-location has changed.
     */
    removeLocationIndexAndSendLocation(index, loc = {}) {
        let resp = {
            status: false,
            hasCurrentLocationChanged: false,
        }

        if (
            Utils.isNull(this.storedLocations) ||
            this.storedLocations.length <= 0 ||
            index < 0
        ) {
            console.log(LOG_PREFIX, "Could not remove the stored-location at index - ", index)
            return resp
        }
        let i = 0
        if (!Utils.isNull(loc) && this.storedLocations.length !== 0) {
            const { latitude, longitude } = loc;
            for (; i < this.storedLocations.length; i++) {
                if (this.storedLocations[i].latitude === latitude && this.storedLocations[i].longitude === longitude) break
            }
        }
        index = i
        resp.status = true
        this.storedLocations.splice(index, 1)

        this.updateLocalStoreForLocations()
        this.sendSelectedLocations()
        let newLocIndex = this.currentLocationIndex
        if (this.storedLocations.length === 0 && index === 0) {
            newLocIndex = -1

        } else if (this.currentLocationIndex === index) {
            newLocIndex = 0
            resp.hasCurrentLocationChanged = true

        } else if (this.currentLocationIndex >= this.storedLocations.length) {
            newLocIndex = this.storedLocations.length - 1
            resp.hasCurrentLocationChanged = true

        } else if (this.currentLocationIndex > index) {
            // NOTE: The original location has not changed. Only the index has. So no need to set that.
            newLocIndex = newLocIndex - 1
        }

        console.log(LOG_PREFIX, "Old loc index - ", this.currentLocationIndex)
        console.log(LOG_PREFIX, "New loc index - ", newLocIndex)

        this.setCurrentLocationIndex(newLocIndex)

        // Update analytics
        Analytics.sendEvent(
            Constants.ANALYTICS.EVENTS.CATEGORY.LOCATION,
            Constants.ANALYTICS.EVENTS.ACTION.REMOVE_LOCATION,
        )

        return resp
    }

    /**
     * Fetch the stored locations
     * @returns {array<WeatherParams>|null}
     */
    getLocationsFromStore() {
        // NOTE: Purposefully not touching the local-store here, since it's not required
        if (Utils.isNull(this.storedLocations) || this.storedLocations.length === 0) {
            return null
        }

        if (Utils.isNull(this.storedLocations) || this.storedLocations.length === 0) {
            return null
        }

        let weatherParamsList = this.storedLocations.map((location) => {
            return (WeatherParams.instanceFromRawObj(location))
        })
        return weatherParamsList
    }

    /**
     * Fetches the only geo-location from the store.
     * @returns {WeatherParams|null}
     */
    getGeoLocationFromStore() {
        if (Utils.isNull(this.geoLocation)) {
            return null
        }

        try {
            let newLoc = WeatherParams.instanceFromRawObj(this.geoLocation)
            return newLoc
        } catch (err) {
            console.log(LOG_PREFIX, "Error creating weather-params instance from the raw geo-location")
            return null
        }
    }

    /**
     * Check if the location already exists in the store
     * @param {weatherParams} location
     * @returns {integer} -1 if the location is absent. Non-negative indicates the position of the location
     */
    isLocationAlreadyPresent(location) {
        if (Utils.isNull(location)) {
            return -1
        }

        for (let i = 0; i < this.storedLocations.length; i++) {
            let sLoc = this.storedLocations[i]
            // Compare locations.
            // NOTE: The comparision used here is purposefully exact.
            // Not using areWeatherAreasEqual here since this function is called for manual locations
            // from the sidebar. In which case, every weather-property needs to be checked.
            if (true === location.isEqualToObj(sLoc)) {
                return i
            }
        }
        return -1
    }

    /**
     * Change the current location index. This will also trigger the callbacks for changed selected location.
     * triggerCbs is an optional argument that'll trigger all the current-location-changed callbacks. It's true by default.
     * Setting it to false will only update the index without triggering any of the callbacks.
     * @returns {boolean} Returns true if able to set successfully else false
     */
    setCurrentLocationIndex(index) {
        if (index >= this.storedLocations.length) {
            return false
        }
        if (this.currentLocationIndex === index) {
            return true
        }

        this.currentLocationIndex = index
        this.updateLocalStoreForCurrentLocationIndex()

        // Update the geo params
        this.isCurrentLocationFromGeoLocation = (index === -1)
        this.updateLocalStoreForIsCurrentLocationGeo()

        // Update analytics
        Analytics.sendEvent(
            Constants.ANALYTICS.EVENTS.CATEGORY.LOCATION,
            Constants.ANALYTICS.EVENTS.ACTION.SELECT_LOCATION,
        )

        if (this.isCurrentLocationFromGeoLocation) {
            Analytics.sendEvent(
                Constants.ANALYTICS.EVENTS.CATEGORY.LOCATION,
                Constants.ANALYTICS.EVENTS.ACTION.SELECT_CURRENT_LOCATION,
            )
        }

        return true
    }

    /**
     * Get the current location object.
     * Returns null if the index is invalid or nothing is in the store
     * @returns {WeatherParams|null}
     */
    getCurrentLocation() {
        if (this.currentLocationIndex < 0 || this.currentLocationIndex >= this.storedLocations.length) {
            console.log(LOG_PREFIX, "Could not fetch the current location since the index is incorrect")
            return null
        }
        let location = this.storedLocations[this.currentLocationIndex]
        return WeatherParams.instanceFromRawObj(location)
    }

    /**
     * Get the current location index.
     * Return -1 if there is no location set
     * @return {integer}
     */
    getCurrentLocationIndex() {
        return this.currentLocationIndex
    }

    /**
     * @returns {boolean} True if current location is from geo-location.
     */
    isCurrentLocationFromGeo() {
        return this.isCurrentLocationFromGeoLocation
    }

    // PRAGMA MARK :- Handle data from local-storage
    /**
     * Update local-storage for only the store-locations data.
     */
    updateLocalStoreForLocations() {
        try {
            if (false === Utils.isNull(DataSource)) {
                DataSource.setItem(Constants.LOCATION_STORE.LOCATION_STORE_KEY, JSON.stringify(this.storedLocations))
            }
        } catch (err) {
            console.log(LOG_PREFIX, "Error setting locations data into DataSource - ", err)
        }

    }

    /**
     * Update local-storage for only the current-location-index.
     */
    updateLocalStoreForCurrentLocationIndex() {
        try {
            if (false === Utils.isNull(DataSource)) {
                DataSource.setItem(Constants.LOCATION_STORE.CURRENT_LOCATION_INDEX, this.currentLocationIndex)
            }
        } catch (err) {
            console.log(LOG_PREFIX, "Error setting location-index data into DataSource -", err)
        }
    }

    /**
     * Update local-storage for geo-data.
     * Updates isCurrentLocationFromGeoLocation and geoLocation data.
     */
    updateLocalStoreForIsCurrentLocationGeo() {
        try {
            if (false === Utils.isNull(DataSource)) {
                DataSource.setItem(Constants.LOCATION_STORE.IS_CURR_LOC_FROM_GEO, this.isCurrentLocationFromGeoLocation)
            }
        } catch (err) {
            console.log(LOG_PREFIX, "Error setting geo-index data into DataSource -", err)
        }
    }

    updateLocalStoreForGeoLocation() {
        try {
            if (false === Utils.isNull(DataSource)) {
                let geoData = null
                if (false === Utils.isNull(this.geoLocation)) {
                    geoData = JSON.stringify(this.geoLocation)
                }
                DataSource.setItem(Constants.LOCATION_STORE.GEO_LOCATION, geoData)
            }
        } catch (err) {
            console.log(LOG_PREFIX, "Error setting geo-index data into DataSource -", err)
        }
    }
}

let instance = new LocationStore()
export default instance



// WEBPACK FOOTER //
// ./src/stores/locationStore.js